/**
 * Header entry point for board automations — sits beside the Share button.
 * A lightning-bolt icon (deliberately NOT a robot) + a count of rules.
 *
 * Self-contained from the `tracker`, so both header sites can just pass
 * `tracker`, exactly like the Share button.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Button } from '@monksflow/monks-ui';
import { Zap } from 'lucide-react';

import type { Tracker } from '@/entities/tracker';
import { trackerAutomationsQueryOptions } from '@/entities/tracker-automation';
import { track } from '@/shared/analytics';

import { useAutomationBoard } from '../../lib/useAutomationBoard';
import { boardTypeOf } from '../../lib/trackers-analytics';
import { AutomationsPanel } from './AutomationsPanel';
import { CountBadge } from '@/shared/ui';

export function AutomationsButton({
  tracker,
  size = 'default',
}: {
  tracker: Tracker;
  /** `lg` is the 40px rung the redesigned board header sits its actions on. */
  size?: 'default' | 'lg';
}) {
  const { t } = useTranslation('trackers');
  const [open, setOpen] = useState(false);
  const boardType = boardTypeOf(tracker.itemSource);
  const { data: automations = [] } = useQuery({
    ...trackerAutomationsQueryOptions(tracker.id),
    enabled: Boolean(tracker.id),
  });

  const { columns, triggerColumns, customGroups } = useAutomationBoard(tracker);

  return (
    <>
      <Button
        variant="outline"
        size={size}
        onClick={() => {
          setOpen(true);
          track('tracker_automations_opened', {
            tracker_id: tracker.id,
            board_type: boardType,
            rule_count: automations.length,
          });
        }}
      >
        <Zap className="size-4" aria-hidden="true" />
        {t('trackersBoard.automationsPanel.automations')}
        <CountBadge count={automations.length} />
      </Button>
      <AutomationsPanel
        open={open}
        onOpenChange={setOpen}
        trackerId={tracker.id}
        boardType={boardType}
        columns={columns}
        triggerColumns={triggerColumns}
        customGroups={customGroups}
      />
    </>
  );
}
